import classNames from 'classnames';

import { MessageBundle } from '@amzn/arb-tools';
import {
  CTA_TYPE,
  Heading,
  HEADING_SIZE,
  HEADING_TAG,
  ListComponent,
  MainBanner,
  PageSection,
  SecondaryBanner,
  SECONDARY_BANNER_VARIANT,
  SECTION_THEME,
  useDeviceSize,
  BentoCards,
  AdvocatesComponent,
} from '@amzn/sss-website-theme';

import { ROUTES } from 'src/enums/Routes';
import { getImageRoot, t } from 'src/helpers';

// eslint-disable-next-line no-restricted-imports
import styles from '../Btf.module.scss';

enum SECTION_IDS {
  CARBON_STANDARD = 'carbon-standard',
  MEASUREMENT = 'measurement',
}

interface SustainableAviationFuelBtfTemplateProps {
  productDetailsBundle: MessageBundle;
  commonBundle: MessageBundle;
  segmentPageBundle: MessageBundle;
}

export const SustainableAviationFuelBtfTemplate = ({
  productDetailsBundle,
  commonBundle,
  segmentPageBundle,
}: SustainableAviationFuelBtfTemplateProps) => {
  const { isSmallDesktop } = useDeviceSize();
  const imageRoot = getImageRoot(isSmallDesktop);

  return (
    <>
      <PageSection theme="squid-ink" className={classNames(styles.pageSection)}>
        <BentoCards
          className={styles.bentoCards}
          isRebrandingActive
          cardsWrapperProps={{
            openNewTabLabel: t(productDetailsBundle, 'newTab_label'),
            sliderPaginationLabel: t(productDetailsBundle, 'of_label'),
            sliderNextAriaLabel: t(productDetailsBundle, 'nextSlide_ariaLabel'),
            sliderPreviousAriaLabel: t(productDetailsBundle, 'previousSlide_ariaLabel'),
          }}
          sourceLabel={t(productDetailsBundle, 'source_label')}
          hideImageCardsOnMobile
          description={t(productDetailsBundle, 'saf_bentoCards_description')}
          title={t(productDetailsBundle, 'saf_bentoCards_title')}
          firstCard={{
            src: `${imageRoot}/saf-bento-1.png`,
            alt: t(productDetailsBundle, 'saf_bentoCards_firstCard_alt'),
          }}
          secondCard={{
            src: `${imageRoot}/saf-bento-2.png`,
            alt: t(productDetailsBundle, 'saf_bentoCards_secondCard_alt'),
          }}
          theme={SECTION_THEME.Dark}
        />
      </PageSection>

      <PageSection className={classNames(styles.pageSection)} theme="medium-spruce" id={SECTION_IDS.CARBON_STANDARD}>
        <SecondaryBanner
          isRebrandingActive
          imagePrimary={{
            src: `${imageRoot}/LCF-standard-1.jpg`,
            alt: t(productDetailsBundle, 'saf_methodology_imagePrimary'),
          }}
          imageSecondary={{
            src: `${imageRoot}/LCF-standard-2.jpg`,
            alt: t(productDetailsBundle, 'saf_methodology_imageSecondary'),
          }}
          title={t(productDetailsBundle, 'saf_methodology_title')}
          description={t(productDetailsBundle, 'saf_methodology_description')}
          theme={SECTION_THEME.Sustainability_Spruce}
          cta={{
            label: t(productDetailsBundle, 'carbonStandard_cta'),
            link: 'https://iscc-system.org/certification/certification-schemes/',
          }}
          variant={SECONDARY_BANNER_VARIANT.LEFT}
        />
      </PageSection>

      <PageSection id={SECTION_IDS.MEASUREMENT} theme="light-spruce">
        <MainBanner
          isRebrandingActive
          image={{
            src: `${imageRoot}/saf-mrv.png`,
            alt: t(productDetailsBundle, 'saf_mrv_alt'),
          }}
          theme={SECTION_THEME.Sustainability_Spruce}
          title={t(productDetailsBundle, 'maritimeFuels_measurement_title')}
          subtitle={t(productDetailsBundle, 'maritimeFuels_measurementIscc_subtitle')}
          accordions={[
            {
              title: t(productDetailsBundle, 'measurement_measurement'),
              content: [
                {
                  text: t(productDetailsBundle, 'lowerCarbonFuels_measurementIscc_content'),
                },
              ],
            },
            {
              title: t(productDetailsBundle, 'measurement_reporting'),
              content: [
                {
                  text: t(productDetailsBundle, 'lowerCarbonFuels_reportingIscc_content'),
                },
              ],
            },
            {
              title: t(productDetailsBundle, 'measurement_verification'),
              content: [
                {
                  text: t(productDetailsBundle, 'lowerCarbonFuels_verificationIscc_content'),
                },
              ],
            },
          ]}
        />
      </PageSection>
      <PageSection>
        <Heading className={styles.learnMoreHeading} as={HEADING_TAG.H3} size={HEADING_SIZE.h3}>
          {t(productDetailsBundle, 'lowerCarbonFuels_learnMoreHeading')}
        </Heading>
        <ListComponent
          isRebrandingActive
          items={[
            {
              title: t(productDetailsBundle, 'lowerCarbonFuels_title'),
              subtitle: t(productDetailsBundle, 'lowerCarbonFuels_subtitle'),
              image: {
                src: `${imageRoot}/LCF-segment-1.jpg`,
                alt: t(productDetailsBundle, 'lowerCarbonFuels_containerShip_alt'),
              },
              cta: {
                href: ROUTES.CREDITS_SEGMENT_LOWER_CARBON_FUELS,
                type: CTA_TYPE.Link,
                forceServerSideNav: false,
                ariaLabelCopy: {
                  exploreText: t(productDetailsBundle, 'learnMore_explore'),
                  newTabText: t(productDetailsBundle, 'learnMore_newTab'),
                  pdfText: t(productDetailsBundle, 'learnMore_pdf'),
                },
              },
              label: t(productDetailsBundle, 'learnMore_exploreScience'),
            },
          ]}
        />
      </PageSection>
      <AdvocatesComponent
        dataTrackingLabel={t(segmentPageBundle, 'advocates_dataTrackingLabel')}
        theme={SECTION_THEME.Squid_Ink}
        isStandAlone
        brandTabs={{
          buttonsAriaLabels: {
            previous: t(commonBundle, 'previous_ariaLabel'),
            next: t(commonBundle, 'next_ariaLabel'),
          },
          options: [
            {
              brandIconImage: {
                src: `${imageRoot}/credits-advocates-logo-3.svg`,
                alt: t(segmentPageBundle, 'advocates_brandIcon_altText_krupp'),
              },
              profileImage: {
                src: `${imageRoot}/credits-advocates-headshot-3.jpg`,
                alt: t(segmentPageBundle, 'advocates_profileImage_altText_krupp'),
              },
              profileName: t(segmentPageBundle, 'advocates_profileName_krupp'),
              profileOccupation: t(segmentPageBundle, 'advocates_profileOccupation_krupp'),
            },
            {
              brandIconImage: {
                src: `${imageRoot}/credits-advocates-logo-2.svg`,
                alt: t(segmentPageBundle, 'advocates_brandIcon_altText_figueres'),
              },
              profileImage: {
                src: `${imageRoot}/credits-advocates-headshot-2.jpg`,
                alt: t(segmentPageBundle, 'advocates_profileImage_altText_figueres'),
              },
              profileName: t(segmentPageBundle, 'advocates_profileName_figueres'),
              profileOccupation: t(segmentPageBundle, 'advocates_profileOccupation_figueres'),
            },
            {
              brandIconImage: {
                src: `${imageRoot}/credits-advocates-logo-1.svg`,
                alt: t(segmentPageBundle, 'advocates_brandIcon_altText_sanjayan'),
              },
              profileImage: {
                src: `${imageRoot}/credits-advocates-headshot-1.jpg`,
                alt: t(segmentPageBundle, 'advocates_profileImage_altText_sanjayan'),
              },
              profileName: t(segmentPageBundle, 'advocates_profileName_sanjayan'),
              profileOccupation: t(segmentPageBundle, 'advocates_profileOccupation_sanjayan'),
            },
          ],
        }}
        quotes={[
          t(segmentPageBundle, 'advocates_quote_krupp'),
          t(segmentPageBundle, 'advocates_quote_figueres'),
          t(segmentPageBundle, 'advocates_quote_sanjayan'),
        ]}
        backgroundImage={{
          src: `${imageRoot}/credits-advocates-1.jpeg`,
          alt: t(segmentPageBundle, 'advocates_backgroundImage_altText'),
        }}
      />
    </>
  );
};
